import type { IApplicantInsightsProps, QualificationDemographic } from "../types";

import ApplicantInsightsCount from "./ApplicantInsightsCount";
import ApplicantInsightsErrorState from "./ApplicantInsightsErrorState";
import { ApplicantInsightsFeedback } from "./ApplicantInsightsFeedback";
import ApplicantInsightsHeader from "./ApplicantInsightsHeader";
import { ApplicantInsightsSkillsHeader } from "./ApplicantInsightsSkillsHeader";
import DemographicChart from "./DemographicChart";
import { Divider, Flex, Box } from "../../../../../../mads";
import { useViewjobOneGraphModel } from "../../../../context/ViewJobDataContext";

import { colors } from "@indeed/ifl-tokens";
import React from "react";

export const flattenDemographics = (
  ...demographics: QualificationDemographic[][]
): QualificationDemographic[] => {
  return demographics.reduce((agg, curr) => {
    return agg.concat(curr);
  }, []);
};

export const ApplicantInsightsContent: React.FunctionComponent<IApplicantInsightsProps> = (
  props: IApplicantInsightsProps,
) => {
  const { count, workExperience, education, skills, certificates } = props;
  const vjModel = useViewjobOneGraphModel();

  const isJsPremiumApplicantInsightsFeedbackTstActive =
    vjModel?.features?.isJsPremiumApplicantInsightsFeedbackTstActive;

  const sortedDemographics = React.useMemo(() => {
    return flattenDemographics(workExperience, education, skills, certificates).sort(
      (a, b) => b.percent - a.percent,
    );
  }, [workExperience, education, skills, certificates]);

  return (
    <Flex
      sx={{
        flexDirection: "column",
      }}
    >
      <Box>
        <ApplicantInsightsHeader showPopover={true} showBranding={true} />
        <ApplicantInsightsCount count={count} />
        {sortedDemographics.length > 0 ? (
          <>
            <ApplicantInsightsSkillsHeader />
            <Box
              sx={{
                borderRadius: 12,
                borderWidth: 1,
                borderColor: colors.border.weak,
                borderStyle: "solid",
                paddingInline: 12,
                paddingBlockStart: 12,
              }}
              data-testid="applicant-insights-demographics"
            >
              {sortedDemographics.map((item) => (
                <DemographicChart key={item.title} item={item} />
              ))}
            </Box>
            {isJsPremiumApplicantInsightsFeedbackTstActive && <ApplicantInsightsFeedback />}
          </>
        ) : (
          <ApplicantInsightsErrorState />
        )}
      </Box>
      <Divider
        sx={{
          marginTop: 32,
          marginBottom: 2,
        }}
      />
    </Flex>
  );
};
